import { SudoButton } from "../../sudokit/components/SudoButton";
import type { PlanStep } from "./repairReducer";

const SEVERITY_BG: Record<PlanStep["severity"], string> = {
  red: "bg-red-50 border-red-200",
  yellow: "bg-yellow-50 border-yellow-200",
  green: "bg-green-50 border-green-200",
};

const STATUS_BADGE: Record<PlanStep["status"], { label: string; className: string }> = {
  pending: { label: "Waiting", className: "bg-gray-100 text-gray-600" },
  ready: { label: "Ready", className: "bg-blue-100 text-blue-700" },
  "in-progress": { label: "In progress", className: "bg-blue-100 text-blue-700" },
  complete: { label: "Done", className: "bg-green-100 text-green-700" },
  skipped: { label: "Skipped", className: "bg-gray-100 text-gray-500" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700" },
};

type Props = {
  step: PlanStep;
  stepIndex: number;
  showPreviewToggle?: boolean;
  previewOpen?: boolean;
  onTogglePreview?: () => void;
  actions?: React.ReactNode;
};

export function RepairStepRow({ step, stepIndex, showPreviewToggle, previewOpen, onTogglePreview, actions }: Props) {
  const disabled = step.status === "pending";
  const { label, className } = STATUS_BADGE[step.status];

  return (
    <div
      className={`border rounded-lg p-3 ${SEVERITY_BG[step.severity]} ${disabled ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-6 h-6 rounded-full bg-white border border-gray-300 text-xs font-medium flex items-center justify-center text-gray-600 shrink-0 mt-0.5">
          {step.status === "complete" ? "✓" : step.status === "skipped" ? "–" : stepIndex + 1}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{step.title}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${className}`}>{label}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
          {step.error && (
            <p className="text-sm text-red-700 mt-2 bg-red-50 border border-red-200 rounded px-2 py-1">
              Error: {step.error}
            </p>
          )}
          {showPreviewToggle && step.preview.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={onTogglePreview}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {previewOpen ? "Hide preview" : `Show preview (${step.preview.length})`}
              </button>
              {previewOpen && (
                <ul className="mt-2 max-h-40 overflow-auto bg-white border border-gray-200 rounded p-2 text-xs text-gray-700">
                  {step.preview.map((line, i) => (
                    <li key={i} className="py-0.5 truncate" title={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        {actions && <div className="flex flex-col gap-1 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

export function skipButton({
  step,
  onSkip,
}: {
  step: PlanStep;
  onSkip: () => void;
}): React.ReactNode {
  if (step.status !== "ready" && step.status !== "pending" && step.status !== "failed") return null;
  // Allow skipping even a required step if it has failed — otherwise a broken
  // step dead-ends the entire flow and forces the admin to re-diagnose.
  if (step.required && step.status !== "failed") return null;
  return (
    <SudoButton variant="tertiary" size="small" onClick={onSkip}>
      Skip
    </SudoButton>
  );
}
